'use client';

/**
 * What the output pane shows while somebody is still typing.
 *
 * The pane used to answer as you typed, which meant a sentence bought a model
 * call at every natural pause between words — each one a partial question,
 * every one of them superseded before it could be read. The scheduler in
 * Translator.tsx already stopped that (see IDLE_MS): typing now waits. This is
 * the other half of that change, and without it the saving is not one anybody
 * would keep.
 *
 * A pane that simply sat there for five seconds is indistinguishable from a
 * pane that has broken, and the reflex it produces is to type again — which
 * restarts the wait and costs the call anyway. So the wait is given a face. It
 * says three things at once, and each of them is load-bearing:
 *
 *   • the answer above is DIM (0.5), so the stale translation still on screen
 *     reads as stale rather than as the answer to what is in the box now;
 *   • something is visibly alive in the middle of the pane, so the page never
 *     reads as having stopped;
 *   • the way out is on screen and one click wide.
 *
 * NOT a modal, deliberately and non-negotiably. A dialog over the page would
 * take the focus out of the textarea and stop the typing it is drawn to
 * accompany, which would make the feature self-defeating: you cannot show
 * somebody a "still typing" animation by interrupting their typing. It is an
 * absolutely-positioned layer inside the output card, `pointer-events-none`
 * everywhere except the one button, so the caret never leaves the box and the
 * answer underneath stays selectable.
 *
 * THE CHARACTER IS THE KHARMA COIN, and it is animated in CSS rather than
 * shipped as a film of itself.
 *
 * It was a bear at a laptop — an original drawing, rendered to two GIFs, one
 * matted to each theme, with a still frame beside each for reduced motion and a
 * preload so the first one did not arrive late. Every one of those was a patch
 * over the format: a GIF cannot be transparent without crawling edges, cannot
 * be paused, and cannot be asked to do anything it was not rendered doing.
 *
 * The coin needs none of them. It is the one piece of artwork the product
 * already has (public/kharma.png — the token every answer and every game is
 * paid in), it has a real alpha channel so one file sits on either pane, and
 * because the MOTION is CSS it stops when the reader has asked for less motion
 * and costs no bytes beyond an image the header has usually already fetched.
 * The choreography is in globals.css under `.gt-coin`; what it is trying to be
 * is written out at the top of that block.
 */

import { useEffect, useState } from 'react';

/** The token artwork: 256×256, transparent outside the rim. */
const COIN = '/kharma.png';

/**
 * Fetch the coin into the browser cache, now, while nothing needs it.
 *
 * Called once from Translator's boot effect. Usually a no-op — the balance in
 * the header draws the same file — but a signed-out visitor has no balance, and
 * the first thing the wait must not do is appear as an empty box.
 */
export function preloadCoin(): void {
  if (typeof window === 'undefined') return;
  const img = new window.Image();
  img.src = COIN;
}

export default function TypingHold({
  seconds,
  onNow,
  compact = false,
  live = false,
}: {
  /**
   * Seconds left on the counted half of the wait, or null during the silent
   * half. Only the wording changes on it — the character and the button are the
   * same throughout, because a layer that rearranged itself halfway through
   * would draw the eye back at the moment it should be settling down.
   */
  seconds: number | null;
  /** Translate now. The same act as Ctrl+Enter, and it runs the same code. */
  onNow: () => void;
  /**
   * There is a live draft under this layer, and it is updating as they type.
   *
   * The layer was designed for an empty pane, or one holding a STALE answer that
   * nobody should be reading — which is why it fills the answer area, centres the
   * coin in it and dims what is underneath. Live drafts make that exactly wrong:
   * the thing under the layer is now the most useful thing on the page, and a
   * coin sitting on top of it is a feature covering a feature.
   *
   * So with a draft on screen the same three parts — character, countdown,
   * escape hatch — move into a strip across the top of the pane. Nothing is
   * removed, because each of them is still doing its job; the coin is what says
   * the pane is alive, and the button is still the only way out of the wait that
   * is on screen rather than on a keyboard.
   */
  compact?: boolean;
  /**
   * A live draft is being fetched while they type, so what is under this layer
   * is provisional-but-current rather than simply the last answer.
   *
   * It changes one line of text and nothing else, but that line is the only
   * thing telling the reader which of the two they are looking at — and with
   * drafts off it would otherwise promise an update that is never coming.
   */
  live?: boolean;
}) {
  /*
   * ⌘ on a Mac, Ctrl everywhere else.
   *
   * Read after mount rather than during render: the server has no idea which
   * one it is, and a label that says Ctrl on the first paint and ⌘ on the
   * second is a hydration mismatch. Ctrl is the honest default for the frame
   * before this lands — it is also what the window handler accepts on either
   * platform.
   */
  const [meta, setMeta] = useState(false);
  useEffect(() => {
    setMeta(/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent));
  }, []);

  const cue = seconds === null
    ? 'Still typing — holding the final translation'
    : `Final translation in ${seconds}s`;

  if (compact) {
    return (
      /*
        A strip across the top of the pane, over a wash rather than over nothing.

        The wash is load-bearing: the draft's first line runs directly under this
        row, and two texts at the same place with no surface between them is
        unreadable. It fades to transparent at the bottom so the draft is only
        ever partly covered and visibly continues past it, rather than starting
        at a hard edge that reads as the answer being cut off.

        `pr-10` keeps clear of the gutter the spinner and the top Copy button
        share — see the two absolutely-positioned spans in Translator that own
        that corner.
      */
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center gap-3
                   bg-gradient-to-b from-ink-100 via-ink-100/95 to-transparent pb-6 pr-10
                   dark:from-ink-800 dark:via-ink-800/95"
      >
        <Coin className="h-[54px] w-[70px] shrink-0" />

        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-ink-500 dark:text-ink-300">{cue}</p>
          {/* Says what the dimmed text underneath IS. Without it a draft that
              changes under the reader looks like an answer that cannot make up
              its mind, rather than one still being written. */}
          <p className="truncate text-[11px] text-ink-400 dark:text-ink-500">
            {live
              ? 'Live draft below — updating as you type'
              : 'Showing the last answer until this one lands'}
          </p>
        </div>

        <button
          type="button"
          onClick={onNow}
          aria-label="Translate now"
          className="gt-hold-btn pointer-events-auto ml-auto inline-flex shrink-0 items-center gap-1.5
                     rounded-full border border-accent-300 bg-white px-3 py-1.5 text-[12px]
                     font-semibold text-accent-700 shadow-sm transition
                     hover:border-accent-400 hover:text-accent-800
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400
                     dark:border-accent-500/50 dark:bg-ink-900 dark:text-accent-300
                     dark:hover:text-accent-200"
        >
          <kbd className="rounded border border-accent-300 px-1 font-sans text-[10px]
                          dark:border-accent-500/50">
            {meta ? '⌘' : 'Ctrl'}
          </kbd>
          <kbd className="rounded border border-accent-300 px-1 font-sans text-[10px]
                          dark:border-accent-500/50">
            Enter
          </kbd>
          <span className="hidden sm:inline">Finish now</span>
        </button>
      </div>
    );
  }

  return (
    /*
      Pinned to the TOP of the pane rather than centred in the whole of it.

      Centring in the block itself is right for an empty pane and wrong for
      every long answer: the middle of a three-screen translation is off the
      bottom of the window, so the one moving thing on the page would be
      somewhere the reader cannot see. The cap centres it in the first part of
      the card — which is where the answer starts, and where the eye already is
      — for panes of any height.

      15rem is not an arbitrary cap. It is the answer area's own minimum height
      (the `min-h-[15rem]` this layer sits over), so the character is centred in
      the SPACE THE ANSWER OCCUPIES rather than in the block — which also
      contains the Copy row underneath it. Centred in the block, the cue line
      landed on the countdown pill in that row: two strings of text at the same
      height, one half over the other.
    */
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-20 flex h-full max-h-[15rem]
                 flex-col items-center justify-center gap-3 px-4"
    >
      <Coin className="h-[140px] w-[180px] shrink-0" />

      <p className="text-center text-[13px] font-medium text-ink-500 dark:text-ink-300">
        {/* The number is the same one in the pill under the answer, and both are
            driven by the same interval, so they cannot disagree. */}
        {cue}
      </p>

      <button
        type="button"
        onClick={onNow}
        aria-label="Translate now"
        /*
          `pointer-events-auto` re-enables clicks on the one element that wants
          them. Everything around it stays transparent to the mouse, so text
          under this layer can still be selected mid-wait.
        */
        className="gt-hold-btn pointer-events-auto relative inline-flex items-center gap-2
                   rounded-full border border-accent-300 bg-white px-3.5 py-2 text-[13px]
                   font-semibold text-accent-700 shadow-sm transition
                   hover:border-accent-400 hover:text-accent-800
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400
                   dark:border-accent-500/50 dark:bg-ink-900 dark:text-accent-300
                   dark:hover:text-accent-200"
      >
        {/*
          The sparks, on the same four-point shape and the same staggered loop
          the Overview toggle uses — see `.gt-spark` in globals.css for why they
          are never in step. They overhang the button on every side, so
          `pointer-events-none` is what stops a star from swallowing the click at
          the exact moment somebody finally tries it.
        */}
        <span className="pointer-events-none absolute -inset-2 z-0" aria-hidden>
          {[
            { top: '0px', left: '2px', size: 9, delay: '0s' },
            { top: '6px', right: '0px', size: 7, delay: '0.45s' },
            { bottom: '2px', left: '14px', size: 6, delay: '0.9s' },
            { bottom: '7px', right: '10px', size: 8, delay: '1.25s' },
          ].map((s, i) => (
            <svg
              key={i}
              viewBox="0 0 24 24"
              className="gt-spark absolute text-amber-400"
              style={{ ...s, width: s.size, height: s.size, animationDelay: s.delay }}
              fill="currentColor"
            >
              <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
            </svg>
          ))}
        </span>

        <kbd
          className="relative z-10 rounded border border-accent-300 px-1 font-sans text-[11px]
                     dark:border-accent-500/50"
        >
          {meta ? '⌘' : 'Ctrl'}
        </kbd>
        <kbd
          className="relative z-10 rounded border border-accent-300 px-1 font-sans text-[11px]
                     dark:border-accent-500/50"
        >
          Enter
        </kbd>
        <span className="relative z-10">Translate now</span>
      </button>

      {/*
        The cue, quiet and one line long.

        It exists because the button above is a keyboard shortcut wearing a
        border, and a shortcut drawn as a button is ambiguous about whether it is
        a label or a control — people read `Ctrl Enter` as instructions and press
        the keys instead of clicking. Saying it plainly costs one line and
        removes the only question the layer raises.
      */}
      <p className="text-center text-[11px] text-ink-400 dark:text-ink-500">
        Click it (or press the keys) to get the translation right away
      </p>
    </div>
  );
}

/**
 * The character: the Kharma coin, tossing itself while it waits.
 *
 * Five nested boxes, one job each, because a CSS element can only run one
 * transform animation and this needs four at once:
 *
 *   stage   the box the layout reserves. Nothing in it moves the page.
 *   shadow  a soft ellipse on the "ground" that shrinks as the coin leaves it —
 *           the cheapest thing there is for making a jump read as height.
 *   hop     up and down, with the squash before take-off and on landing.
 *   spin    the flip itself, around the vertical axis, in perspective.
 *   face    the artwork, with a band of light that crosses it once it is still.
 *
 * `alt=""` and aria-hidden: everything the animation says is said in words by
 * the countdown pill under the answer, which is already an aria-live region.
 *
 * Exported because the global waiting dialog (GlobalWait.tsx) stars the same
 * character: one coin, one toss, wherever the app says "working on it".
 */
export function Coin({ className }: { className: string }) {
  return (
    <span className={`gt-coin ${className}`} aria-hidden>
      <span className="gt-coin-shadow" />
      <span className="gt-coin-hop">
        <span className="gt-coin-spin">
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative,
              pre-sized art; next/image would add a wrapper between the spin and
              the thing being spun. */}
          <img src={COIN} alt="" width={256} height={256} draggable={false} />
          <span className="gt-coin-shine" />
        </span>
      </span>
      {/* Three sparks on the loop the buttons already use, each on its own
          delay so they are never in step. */}
      {[
        { top: '8%', left: '12%', size: '13%', delay: '0.2s' },
        { top: '26%', right: '10%', size: '10%', delay: '1.1s' },
        { bottom: '22%', left: '18%', size: '8%', delay: '1.9s' },
      ].map(({ size, delay, ...at }, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className="gt-spark gt-coin-spark"
          style={{ ...at, width: size, animationDelay: delay }}
          fill="currentColor"
        >
          <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
        </svg>
      ))}
    </span>
  );
}
